export const LANGUAGE_SOURCE = "es";
export const LANGUAGE_STORAGE_KEY = "nitro-language";
export const LANGUAGE_EVENT = "nitro-language-change";

export const LANGUAGE_OPTIONS = Object.freeze([
  Object.freeze({ code: "en", label: "English", note: "Automatic translation is enabled. Some simracing terms may be translated incorrectly." }),
  Object.freeze({ code: "fr", label: "Français", note: "La traduction automatique est activée. Certains termes de simracing peuvent être traduits de façon incorrecte." }),
  Object.freeze({ code: "de", label: "Deutsch", note: "Die automatische Übersetzung ist aktiviert. Einige Simracing-Begriffe können falsch übersetzt werden." }),
  Object.freeze({ code: "it", label: "Italiano", note: "La traduzione automatica è attiva. Alcuni termini del simracing potrebbero essere tradotti in modo errato." }),
  Object.freeze({ code: "pt", label: "Português", note: "A tradução automática está ativada. Alguns termos de simracing podem ser traduzidos incorretamente." }),
  Object.freeze({ code: "ru", label: "Русский", note: "Включен автоматический перевод. Некоторые термины симрейсинга могут быть переведены неточно." }),
]);

const LANGUAGE_CODES = new Set([LANGUAGE_SOURCE, ...LANGUAGE_OPTIONS.map(({ code }) => code)]);
const DEFAULT_PROVIDER_TIMEOUT_MS = 5000;
const DEFAULT_SELECT_WAIT_MS = 350;
const DEFAULT_SELECT_ATTEMPTS = 8;

export const browserLanguageRuntime = createLazyBrowserLanguageRuntime();

export function normalizeLanguageCode(value) {
  const code = String(value || "").trim().toLowerCase();
  return LANGUAGE_CODES.has(code) ? code : LANGUAGE_SOURCE;
}

export function createBrowserLanguageRuntime(options = {}) {
  const windowRef = options.windowRef ?? globalThis.window;
  const documentRef = options.documentRef ?? globalThis.document;
  const MutationObserverRef = options.MutationObserverRef ?? globalThis.MutationObserver;
  const providerTimeoutMs = positiveInteger(options.providerTimeoutMs, DEFAULT_PROVIDER_TIMEOUT_MS);
  const selectWaitMs = positiveInteger(options.selectWaitMs, DEFAULT_SELECT_WAIT_MS);
  const selectAttempts = positiveInteger(options.selectAttempts, DEFAULT_SELECT_ATTEMPTS);
  const reloadPage = options.reload ?? (() => windowRef.location.reload());
  let activeSuppress = () => {};
  let appliedLanguage = LANGUAGE_SOURCE;

  function requireWindow() {
    if (!windowRef || !documentRef) {
      throw runtimeError("LANGUAGE_PROVIDER_UNAVAILABLE", "El traductor solo está disponible en el navegador.");
    }
  }

  function read() {
    requireWindow();
    try {
      return normalizeLanguageCode(windowRef.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    } catch {
      throw runtimeError("LANGUAGE_STORAGE_READ_FAILED", "No pudimos leer tu preferencia de idioma.");
    }
  }

  function readApplied() {
    requireWindow();
    return appliedLanguage;
  }

  function write(language) {
    requireWindow();
    const code = assertLanguage(language);
    try {
      windowRef.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      throw runtimeError("LANGUAGE_STORAGE_WRITE_FAILED", "No pudimos guardar tu preferencia de idioma.");
    }
    return code;
  }

  function snapshot() {
    requireWindow();
    let storageValue;
    try {
      storageValue = windowRef.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      throw runtimeError("LANGUAGE_STORAGE_READ_FAILED", "No pudimos leer tu preferencia de idioma.");
    }
    const select = documentRef.querySelector(".goog-te-combo");
    return Object.freeze({
      appliedLanguage,
      googleCookie: readCookie(documentRef.cookie, "googtrans"),
      selectValue: select?.value ?? null,
      storageValue,
    });
  }

  async function restore(savedState, { signal } = {}) {
    requireWindow();
    throwIfAborted(signal);
    if (!savedState || typeof savedState !== "object") throw new TypeError("savedState is required");
    let storageError = null;
    try { restoreStorageValue(windowRef.localStorage, savedState.storageValue); } catch (error) { storageError = error; }
    try { restoreGoogleState({ documentRef, savedState, windowRef }); } catch { /* best effort */ }
    appliedLanguage = normalizeLanguageCode(savedState.appliedLanguage);
    throwIfAborted(signal);
    if (storageError) throw storageError;
    return normalizeLanguageCode(savedState.storageValue);
  }

  function reload() {
    requireWindow();
    reloadPage();
  }

  function subscribe(listener) {
    requireWindow();
    if (typeof listener !== "function") throw new TypeError("listener is required");
    const onChange = (event) => listener(normalizeLanguageCode(event?.detail?.language));
    windowRef.addEventListener(LANGUAGE_EVENT, onChange);
    return () => windowRef.removeEventListener(LANGUAGE_EVENT, onChange);
  }

  function emit(language) {
    requireWindow();
    const code = assertLanguage(language);
    appliedLanguage = code;
    windowRef.dispatchEvent(new windowRef.CustomEvent(LANGUAGE_EVENT, { detail: { language: code } }));
    return code;
  }

  async function start({ containerId, languages = LANGUAGE_OPTIONS.map(({ code }) => code), signal } = {}) {
    requireWindow();
    throwIfAborted(signal);
    const targetId = String(containerId || "").trim();
    if (!targetId) throw new TypeError("containerId is required");
    const includedLanguages = normalizeLanguageList(languages);
    const target = documentRef.getElementById(targetId);
    if (!target) {
      throw runtimeError("LANGUAGE_PROVIDER_CONTAINER_MISSING", "No encontramos el contenedor seguro del traductor.");
    }

    const suppression = createChromeSuppression({ documentRef, MutationObserverRef });
    activeSuppress = suppression.suppress;
    const cleanup = () => {
      signal?.removeEventListener("abort", cleanup);
      suppression.cleanup();
      if (activeSuppress === suppression.suppress) activeSuppress = () => {};
      try { target.replaceChildren(); } catch { /* detached target */ }
    };
    signal?.addEventListener("abort", cleanup, { once: true });

    try {
      await ensureGoogleProvider({ documentRef, signal, timeoutMs: providerTimeoutMs, windowRef });
      throwIfAborted(signal);
      const TranslateElement = windowRef.google?.translate?.TranslateElement;
      if (typeof TranslateElement !== "function") throw providerUnavailable();
      target.replaceChildren();
      new TranslateElement(
        { autoDisplay: false, includedLanguages: includedLanguages.join(","), pageLanguage: LANGUAGE_SOURCE },
        targetId,
      );
      suppression.suppress();
      return true;
    } catch (error) {
      cleanup();
      if (isAbortError(error)) throw error;
      if (error?.code) throw error;
      throw runtimeError("LANGUAGE_PROVIDER_START_FAILED", "No pudimos iniciar el proveedor de traducción.", error);
    }
  }

  async function apply(language, { signal } = {}) {
    requireWindow();
    const code = assertLanguage(language);
    if (code === LANGUAGE_SOURCE) return reset({ signal });
    throwIfAborted(signal);
    let select = null;
    for (let attempt = 0; attempt < selectAttempts; attempt += 1) {
      throwIfAborted(signal);
      select = documentRef.querySelector(".goog-te-combo");
      if (select) break;
      if (attempt < selectAttempts - 1) await abortableDelay(windowRef, selectWaitMs, signal);
    }
    if (!select) throw providerUnavailable();
    throwIfAborted(signal);
    const savedState = { googleCookie: readCookie(documentRef.cookie, "googtrans"), selectValue: select.value };
    try {
      writeGoogleCookie({ documentRef, language: code, windowRef });
      select.value = code;
      select.dispatchEvent(new windowRef.Event("change", { bubbles: true }));
      await abortableDelay(windowRef, 150, signal);
      activeSuppress();
      await abortableDelay(windowRef, 550, signal);
      activeSuppress();
      return code;
    } catch (error) {
      try { restoreGoogleState({ documentRef, savedState, windowRef }); } catch { /* best effort */ }
      throw error;
    }
  }

  async function reset({ signal } = {}) {
    requireWindow();
    throwIfAborted(signal);
    const select = documentRef.querySelector(".goog-te-combo");
    const savedState = { googleCookie: readCookie(documentRef.cookie, "googtrans"), selectValue: select?.value ?? null };
    try {
      writeGoogleCookie({ documentRef, language: null, windowRef });
      if (select) {
        select.value = LANGUAGE_SOURCE;
        select.dispatchEvent(new windowRef.Event("change", { bubbles: true }));
      }
      throwIfAborted(signal);
      return LANGUAGE_SOURCE;
    } catch (error) {
      try { restoreGoogleState({ documentRef, savedState, windowRef }); } catch { /* best effort */ }
      throw error;
    }
  }

  return Object.freeze({ apply, emit, read, readApplied, reload, reset, restore, snapshot, start, subscribe, write });
}

export function createMemoryLanguageRuntime({ applyDelayMs = 450, initialAppliedLanguage = LANGUAGE_SOURCE, initialLanguage = LANGUAGE_SOURCE } = {}) {
  let storedLanguage = normalizeLanguageCode(initialLanguage);
  let appliedLanguage = normalizeLanguageCode(initialAppliedLanguage);
  let failure = "";
  const listeners = new Set();
  const auditListeners = new Set();
  const counters = { applies: 0, cancellations: 0, emits: 0, reads: 0, reloads: 0, resets: 0, restores: 0, starts: 0, writes: 0 };
  let snapshot;

  function publish() {
    snapshot = Object.freeze({ ...counters, appliedLanguage, failure, storedLanguage });
    auditListeners.forEach((listener) => listener());
  }

  function consumeFailure(kind, code, message) {
    if (failure !== kind) return;
    failure = "";
    publish();
    throw runtimeError(code, message);
  }

  function read() {
    counters.reads += 1;
    publish();
    consumeFailure("storage-read", "LANGUAGE_STORAGE_READ_FAILED", "Fixture: lectura bloqueada.");
    return storedLanguage;
  }

  function readApplied() {
    return appliedLanguage;
  }

  function write(nextLanguage) {
    counters.writes += 1;
    publish();
    consumeFailure("storage-write", "LANGUAGE_STORAGE_WRITE_FAILED", "Fixture: escritura bloqueada.");
    storedLanguage = assertLanguage(nextLanguage);
    publish();
    return storedLanguage;
  }

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function emit(nextLanguage) {
    appliedLanguage = assertLanguage(nextLanguage);
    counters.emits += 1;
    publish();
    listeners.forEach((listener) => listener(appliedLanguage));
    return appliedLanguage;
  }

  async function start({ signal } = {}) {
    counters.starts += 1;
    publish();
    throwIfAborted(signal);
    consumeFailure("provider-start", "LANGUAGE_PROVIDER_START_FAILED", "Fixture: proveedor no disponible.");
    await Promise.resolve();
    throwIfAborted(signal);
    return true;
  }

  async function apply(nextLanguage, { signal } = {}) {
    counters.applies += 1;
    publish();
    consumeFailure("provider-apply", "LANGUAGE_PROVIDER_UNAVAILABLE", "Fixture: traducción no disponible.");
    try {
      await abortableDelay(globalThis, applyDelayMs, signal);
      throwIfAborted(signal);
      return assertLanguage(nextLanguage);
    } catch (error) {
      if (isAbortError(error)) {
        counters.cancellations += 1;
        publish();
      }
      throw error;
    }
  }

  async function reset({ signal } = {}) {
    counters.resets += 1;
    publish();
    consumeFailure("provider-reset", "LANGUAGE_PROVIDER_RESET_FAILED", "Fixture: no se pudo restaurar Español.");
    throwIfAborted(signal);
    return LANGUAGE_SOURCE;
  }

  function snapshotState() {
    return Object.freeze({ appliedLanguage, storageValue: storedLanguage });
  }

  async function restore(savedState, { signal } = {}) {
    counters.restores += 1;
    publish();
    throwIfAborted(signal);
    storedLanguage = normalizeLanguageCode(savedState?.storageValue);
    appliedLanguage = normalizeLanguageCode(savedState?.appliedLanguage);
    publish();
    return appliedLanguage;
  }

  function reload() {
    counters.reloads += 1;
    publish();
  }

  function failNext(kind) {
    if (!["storage-read", "storage-write", "provider-start", "provider-apply", "provider-reset"].includes(kind)) {
      throw new TypeError("Unsupported fixture failure");
    }
    failure = kind;
    publish();
  }

  function seedPreference(nextLanguage) {
    storedLanguage = assertLanguage(nextLanguage);
    publish();
  }

  function subscribeAudit(listener) {
    auditListeners.add(listener);
    return () => auditListeners.delete(listener);
  }

  publish();
  return Object.freeze({
    apply,
    audit: Object.freeze({ failNext, getSnapshot: () => snapshot, seedPreference, subscribe: subscribeAudit }),
    emit,
    reload,
    read,
    readApplied,
    reset,
    restore,
    snapshot: snapshotState,
    start,
    subscribe,
    write,
  });
}

export function languageRuntimeErrorKind(error) {
  const code = String(error?.code || "");
  if (code.startsWith("LANGUAGE_STORAGE_")) return "storage";
  if (code.startsWith("LANGUAGE_PROVIDER_")) return "provider";
  if (isAbortError(error)) return "aborted";
  return "unknown";
}

export async function runLanguageSelection({ code, onApplied, providerReady, runtime, signal }) {
  const language = assertLanguage(code);
  if (!runtime || typeof onApplied !== "function") throw new TypeError("runtime and onApplied are required");

  if (language === LANGUAGE_SOURCE) {
    const savedState = runtime.snapshot();
    try {
      await runtime.reset({ signal });
      throwIfAborted(signal);
      runtime.write(language);
      throwIfAborted(signal);
    } catch (error) {
      try { await runtime.restore(savedState); } catch { /* best effort */ }
      throw error;
    }
    onApplied(language);
    runtime.emit(language);
    runtime.reload();
    return Object.freeze({ language, persisted: true });
  }

  await providerReady;
  throwIfAborted(signal);
  await runtime.apply(language, { signal });
  throwIfAborted(signal);
  onApplied(language);
  runtime.emit(language);
  try {
    runtime.write(language);
    return Object.freeze({ language, persisted: true });
  } catch (error) {
    return Object.freeze({ error, language, persisted: false });
  }
}

export async function restoreStoredLanguage({ onApplied, providerReady, runtime, signal }) {
  if (!runtime || typeof onApplied !== "function") throw new TypeError("runtime and onApplied are required");
  const applied = normalizeLanguageCode(runtime.readApplied());
  const stored = normalizeLanguageCode(runtime.read());
  onApplied(applied);
  await providerReady;
  throwIfAborted(signal);
  if (stored === applied) {
    return Object.freeze({ appliedLanguage: applied, restored: false, storedLanguage: stored });
  }
  if (stored === LANGUAGE_SOURCE) await runtime.reset({ signal });
  else await runtime.apply(stored, { signal });
  throwIfAborted(signal);
  onApplied(stored);
  runtime.emit(stored);
  return Object.freeze({ appliedLanguage: stored, restored: true, storedLanguage: stored });
}

function createLazyBrowserLanguageRuntime() {
  let runtime = null;
  const current = () => {
    runtime ||= createBrowserLanguageRuntime();
    return runtime;
  };
  return Object.freeze({
    apply: (...args) => current().apply(...args),
    emit: (...args) => current().emit(...args),
    read: (...args) => current().read(...args),
    readApplied: (...args) => current().readApplied(...args),
    reload: (...args) => current().reload(...args),
    reset: (...args) => current().reset(...args),
    restore: (...args) => current().restore(...args),
    snapshot: (...args) => current().snapshot(...args),
    start: (...args) => current().start(...args),
    subscribe: (...args) => current().subscribe(...args),
    write: (...args) => current().write(...args),
  });
}

function normalizeLanguageList(languages) {
  const values = Array.from(new Set((languages || []).map((value) => assertLanguage(value)).filter((code) => code !== LANGUAGE_SOURCE)));
  if (!values.length) throw new TypeError("languages must include at least one target language");
  return values;
}

function assertLanguage(value) {
  const code = String(value || "").trim().toLowerCase();
  if (!LANGUAGE_CODES.has(code)) throw runtimeError("LANGUAGE_UNSUPPORTED", "El idioma seleccionado no está disponible.");
  return code;
}

function positiveInteger(value, fallback) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : fallback;
}

function runtimeError(code, message, cause) {
  const error = new Error(message, cause ? { cause } : undefined);
  error.code = code;
  return error;
}

function providerUnavailable() {
  return runtimeError("LANGUAGE_PROVIDER_UNAVAILABLE", "El proveedor de traducción no respondió. Inténtalo de nuevo.");
}

function throwIfAborted(signal) {
  if (!signal?.aborted) return;
  throw signal.reason instanceof Error ? signal.reason : abortError();
}

function abortError() {
  const error = new Error("La operación de idioma fue cancelada.");
  error.name = "AbortError";
  return error;
}

function isAbortError(error) {
  return error?.name === "AbortError";
}

function abortableDelay(timerHost, delayMs, signal) {
  throwIfAborted(signal);
  return new Promise((resolve, reject) => {
    const timeout = timerHost.setTimeout(finish, delayMs);
    function finish() {
      signal?.removeEventListener("abort", cancel);
      resolve();
    }
    function cancel() {
      timerHost.clearTimeout(timeout);
      signal?.removeEventListener("abort", cancel);
      reject(signal.reason instanceof Error ? signal.reason : abortError());
    }
    signal?.addEventListener("abort", cancel, { once: true });
  });
}

function ensureGoogleProvider({ documentRef, signal, timeoutMs, windowRef }) {
  throwIfAborted(signal);
  if (typeof windowRef.google?.translate?.TranslateElement === "function") return Promise.resolve();

  return new Promise((resolve, reject) => {
    let settled = false;
    let script = documentRef.querySelector('script[data-nitro-google-translate="true"]');
    let createdScript = false;
    const previousInit = windowRef.googleTranslateElementInit;
    const timeout = windowRef.setTimeout(() => finish(providerUnavailable()), timeoutMs);

    function cleanup() {
      windowRef.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      script?.removeEventListener?.("error", onError);
      script?.removeEventListener?.("load", onLoad);
      if (windowRef.googleTranslateElementInit === onProviderReady) {
        if (previousInit === undefined) delete windowRef.googleTranslateElementInit;
        else windowRef.googleTranslateElementInit = previousInit;
      }
    }

    function finish(error) {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) {
        if (createdScript) script?.remove?.();
        reject(error);
      } else {
        resolve();
      }
    }

    function onProviderReady() {
      if (signal?.aborted) return finish(signal.reason instanceof Error ? signal.reason : abortError());
      if (typeof windowRef.google?.translate?.TranslateElement !== "function") return finish(providerUnavailable());
      finish();
    }

    function onAbort() {
      finish(signal.reason instanceof Error ? signal.reason : abortError());
    }

    function onError() {
      finish(runtimeError("LANGUAGE_PROVIDER_LOAD_FAILED", "No pudimos cargar el proveedor de traducción."));
    }

    function onLoad() {
      if (typeof windowRef.google?.translate?.TranslateElement === "function") finish();
    }

    windowRef.googleTranslateElementInit = onProviderReady;
    signal?.addEventListener("abort", onAbort, { once: true });
    if (!script) {
      script = documentRef.createElement("script");
      createdScript = true;
      script.async = true;
      script.dataset.nitroGoogleTranslate = "true";
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      documentRef.body.appendChild(script);
    }
    script.addEventListener?.("error", onError, { once: true });
    script.addEventListener?.("load", onLoad, { once: true });
  });
}

function createChromeSuppression({ documentRef, MutationObserverRef }) {
  const bodyTop = documentRef.body?.style?.top || "";
  const rootMargin = documentRef.documentElement?.style?.marginTop || "";
  const altered = new Map();
  const selectors = [
    ".goog-te-banner-frame",
    ".goog-te-banner-frame.skiptranslate",
    "iframe.goog-te-banner-frame",
    "body > .skiptranslate",
    ".VIpgJd-ZVi9od-ORHb-OEVmcd",
    ".goog-te-balloon-frame",
    "#goog-gt-tt",
  ];

  function suppress() {
    if (documentRef.body?.style) documentRef.body.style.top = "0";
    if (documentRef.documentElement?.style) documentRef.documentElement.style.marginTop = "0";
    selectors.forEach((selector) => {
      documentRef.querySelectorAll(selector).forEach((element) => {
        if (!altered.has(element)) {
          altered.set(element, {
            display: element.style.display,
            height: element.style.height,
            visibility: element.style.visibility,
          });
        }
        element.style.display = "none";
        element.style.visibility = "hidden";
        element.style.height = "0";
      });
    });
  }

  suppress();
  const observer = typeof MutationObserverRef === "function" && documentRef.body
    ? new MutationObserverRef(suppress)
    : null;
  observer?.observe(documentRef.body, { childList: true, subtree: true });

  function cleanup() {
    observer?.disconnect();
    if (documentRef.body?.style) documentRef.body.style.top = bodyTop;
    if (documentRef.documentElement?.style) documentRef.documentElement.style.marginTop = rootMargin;
    altered.forEach((styles, element) => {
      element.style.display = styles.display;
      element.style.visibility = styles.visibility;
      element.style.height = styles.height;
    });
    altered.clear();
  }

  return Object.freeze({ cleanup, suppress });
}

function writeGoogleCookie({ documentRef, language, windowRef }) {
  const value = language ? `/es/${language}` : "";
  const days = language ? 365 : -1;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const cookieValue = `googtrans=${value}; expires=${expires}; path=/; SameSite=Lax`;
  try {
    documentRef.cookie = cookieValue;
    const hostname = String(windowRef.location?.hostname || "").toLowerCase();
    if (hostname === "nitrosimracing.com.co" || hostname.endsWith(".nitrosimracing.com.co")) {
      documentRef.cookie = `${cookieValue}; domain=.nitrosimracing.com.co`;
    }
  } catch {
    throw runtimeError("LANGUAGE_PROVIDER_COOKIE_FAILED", "El navegador bloqueó la preferencia del traductor.");
  }
}

function readCookie(cookieString, name) {
  const prefix = `${name}=`;
  const part = String(cookieString || "").split(";").map((value) => value.trim()).find((value) => value.startsWith(prefix));
  return part ? part.slice(prefix.length) : null;
}

function restoreGoogleState({ documentRef, savedState, windowRef }) {
  writeGoogleCookieValue({ documentRef, value: savedState.googleCookie, windowRef });
  const select = documentRef.querySelector(".goog-te-combo");
  if (select && savedState.selectValue !== null && savedState.selectValue !== undefined) {
    select.value = savedState.selectValue;
    select.dispatchEvent(new windowRef.Event("change", { bubbles: true }));
  }
}

function writeGoogleCookieValue({ documentRef, value, windowRef }) {
  if (value === null || value === undefined || value === "") {
    writeGoogleCookie({ documentRef, language: null, windowRef });
    return;
  }
  const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
  const cookieValue = `googtrans=${value}; expires=${expires}; path=/; SameSite=Lax`;
  documentRef.cookie = cookieValue;
  const hostname = String(windowRef.location?.hostname || "").toLowerCase();
  if (hostname === "nitrosimracing.com.co" || hostname.endsWith(".nitrosimracing.com.co")) {
    documentRef.cookie = `${cookieValue}; domain=.nitrosimracing.com.co`;
  }
}

function restoreStorageValue(storage, value) {
  try {
    if (value === null || value === undefined) storage.removeItem(LANGUAGE_STORAGE_KEY);
    else storage.setItem(LANGUAGE_STORAGE_KEY, value);
  } catch {
    throw runtimeError("LANGUAGE_STORAGE_RESTORE_FAILED", "No pudimos restaurar tu preferencia de idioma anterior.");
  }
}
